import React from "react";
import { QRCodeCanvas } from "qrcode.react";

// Number to words (Indian)
const ones = ["", "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE", "TEN",
  "ELEVEN", "TWELVE", "THIRTEEN", "FOURTEEN", "FIFTEEN", "SIXTEEN", "SEVENTEEN", "EIGHTEEN", "NINETEEN"];
const tens = ["", "", "TWENTY", "THIRTY", "FORTY", "FIFTY", "SIXTY", "SEVENTY", "EIGHTY", "NINETY"];
function twoDigit(n) {
  if (n < 20) return ones[n];
  return (tens[Math.floor(n / 10)] + (n % 10 ? "-" + ones[n % 10] : "")).trim();
}
function numToWords(n) {
  n = Math.floor(Number(n));
  if (n === 0) return "ZERO";
  let s = "";
  if (n >= 10000000) { s += numToWords(Math.floor(n / 10000000)) + " CRORE "; n %= 10000000; }
  if (n >= 100000) { s += numToWords(Math.floor(n / 100000)) + " LAKH "; n %= 100000; }
  if (n >= 1000) { s += numToWords(Math.floor(n / 1000)) + " THOUSAND "; n %= 1000; }
  if (n >= 100) { s += ones[Math.floor(n / 100)] + " HUNDRED "; n %= 100; }
  if (n) s += twoDigit(n);
  return s.trim();
}

export default function Receipt({ bill, shop }) {
  if (!bill || !shop) return null;
  const date = new Date(bill.created_at);
  const dStr = date.toLocaleDateString("en-IN");
  const tStr = date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

  // UPI QR string with bill amount
  const upi = shop.upi_id
    ? `upi://pay?pa=${shop.upi_id}&pn=${encodeURIComponent(shop.upi_payee_name || shop.name)}&am=${bill.net_total}&cu=INR&tn=${encodeURIComponent("Bill " + bill.bill_no)}`
    : `Bill: ${bill.bill_no} | ₹${bill.net_total}`;

  const hasDiscount = Number(bill.discount || 0) > 0;

  return (
    <div className="receipt-paper printable mx-auto border border-gray-300" style={{ width: 360, padding: 18 }} data-testid="receipt-printable">
      <div className="border-2 border-black/80 rounded p-3">
        <div className="flex justify-between text-[10px]">
          <div>GSTIN: {shop.gstin || ""}</div>
          <div>📞 {shop.phone || ""}</div>
        </div>
        <div className="text-center font-heading text-2xl font-bold tracking-wide mt-1">
          {shop.name?.toUpperCase()}
        </div>
        <div className="text-center text-xs">{shop.address || ""}</div>
        <div className="dashed-divider"></div>
        <div className="text-center text-xs">Invoice</div>
        <div className="dashed-divider"></div>
        <div className="flex justify-between text-[11px]">
          <div>
            <div>Bill No : <b>{bill.bill_no}</b></div>
            <div className="mt-2">Customer Details :</div>
            <div>Name : {bill.customer_name || "-"}</div>
            {bill.customer_address && <div>Address : {bill.customer_address}</div>}
            <div>Contact : {bill.customer_phone || "-"}</div>
          </div>
          <div className="text-right">
            <div>Date : {dStr}</div>
            <div>Time : {tStr}</div>
          </div>
        </div>
        <div className="dashed-divider"></div>
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-black/60">
              <th className="text-left py-1">Sr.</th>
              <th className="text-left py-1">Product</th>
              <th className="text-right py-1">MRP</th>
              <th className="text-right py-1">Qty</th>
              <th className="text-right py-1">Rate</th>
              <th className="text-right py-1">Amount</th>
            </tr>
          </thead>
          <tbody>
            {bill.items.map((it, i) => (
              <tr key={i} className="border-b border-dashed border-gray-300">
                <td className="py-1">{i + 1}</td>
                <td className="py-1">{it.name}</td>
                <td className="text-right">{Math.round(Number(it.mrp) || 0)}</td>
                <td className="text-right">{Number(it.qty).toFixed(0)}</td>
                <td className="text-right">{Math.round(Number(it.rate))}</td>
                <td className="text-right">{Math.round(Number(it.amount))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="dashed-divider"></div>
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <div className="border border-black/40 rounded p-2 flex flex-col items-center justify-center">
            <div className="font-bold">YOU SAVED</div>
            <div className="text-lg font-bold">₹ {Math.round(Number(bill.savings))}</div>
          </div>
          <div>
            <div className="flex justify-between"><span>Total :</span><b>{Math.round(Number(bill.subtotal))}</b></div>
            {hasDiscount && (
              <div className="flex justify-between"><span>Bill Discount</span><span>-{Math.round(Number(bill.discount))}</span></div>
            )}
            {Number(bill.points_redeemed || 0) > 0 && (
              <div className="flex justify-between"><span>LP Used</span><span>-{Number(bill.points_redeemed)}</span></div>
            )}
            <div className="flex justify-between border-t border-black/40 mt-1 pt-1 font-bold"><span>Net Total</span><span>{Math.round(Number(bill.net_total))}</span></div>
          </div>
        </div>
        <div className="dashed-divider"></div>
        <div className="text-[10px]">Terms &amp; Conditions: {shop.terms}</div>
        <div className="text-center text-xs italic mt-2">Thank You … Visit Again !</div>
        <div className="dashed-divider"></div>
        <div className="grid grid-cols-2 gap-3 mt-2 items-center">
          <div className="flex flex-col items-center text-[11px]">
            <QRCodeCanvas value={upi} size={170} data-testid="receipt-upi-qr" />
            <div className="mt-1 font-bold">Scan to Pay ₹{Math.round(Number(bill.net_total))}</div>
          </div>
          <div className="text-[10px] border border-black/40 rounded">
            <div className="flex justify-between border-b border-black/30 px-1"><span>Payment</span><b>{(bill.payments && bill.payments.length > 1 ? "SPLIT" : (bill.payments?.[0]?.method || bill.payment_method)).toUpperCase()}</b></div>
            {(bill.payments || [{method: bill.payment_method, amount: bill.net_total}]).map((p, i) => (
              <div key={i} className="flex justify-between border-b border-black/30 px-1"><span>{p.method.toUpperCase()}</span><span>₹{Math.round(Number(p.amount))}</span></div>
            ))}
            <div className="flex justify-between border-b border-black/30 px-1"><span>LP Earned</span><span>+{bill.points_earned || 0}</span></div>
            {(bill.points_redeemed || 0) > 0 && (
              <div className="flex justify-between border-b border-black/30 px-1"><span>LP Used</span><span>-{bill.points_redeemed}</span></div>
            )}
            <div className="flex justify-between px-1 font-bold"><span>LP Balance</span><span>{bill.points_balance_after ?? 0}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
